/**
 * Approvals — the queue of bank slips and LankaQR receipts, with a sticky
 * panel for the one being looked at.
 *
 * Oldest first, because staff work down the list and a slip sent on Saturday
 * must not sink under Monday's. The panel stays while the list scrolls: the
 * proof image and the amount owed are what the decision rests on.
 *
 * Approve, Different amount and Reject all come from the pure module, so what
 * the panel promises ("Rs. 4,500.00 stays open as Partly paid") is what the
 * API is being asked to do.
 *
 * Spec: DESIGN-BRIEF.md §F and `vApprovals()` in prototype.html.
 */
import React, {useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {useSearchParams} from 'react-router-dom';
import NetlyPayRepository from '../../../internals/repository/NetlyPayRepository';
import {money, type NetlyPayData} from '../../../internals/managers/NetlyPayManager';
import {
  REJECT_REASONS,
  approvalPanel,
  approvalQueue,
  bulkApprovePlan,
  differentAmountNote,
  queueSummary,
  rejectReason,
  selectionTotal,
} from '../approvals';
import {useNetlyPayFetch, useNetlyPayLink} from '../hooks/useNetlyPay';
import {EmptyState, ErrorState, Skeleton} from '../components/States';
import {GhostButton, Modal, PrimaryButton} from '../components/Overlays';
import {ScreenGate, Toast} from '../components/ScreenGate';

const th = 'px-4 py-2.5 text-left text-[12px] font-semibold text-text3 whitespace-nowrap';
const td = 'px-4 py-3 text-[13px] text-text1 align-middle';
const inputCls =
  'h-10 px-3 rounded-lg border border-borderc2 bg-surface text-[13px] text-text1 outline-none focus:border-brand transition-colors';

/**
 * The proof itself.
 *
 * There are no image assets in the handoff and the API returns a description
 * rather than a URL for now, so this is a striped placeholder carrying the
 * proof's own label — the shape the real upload will slot into.
 */
const Proof = ({label, height, onClick}: {label: string; height: number; onClick?: () => void}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    aria-label={onClick ? `Open ${label} full size` : label}
    className="w-full rounded-xl border border-borderc overflow-hidden flex items-end p-2 text-left disabled:cursor-default"
    style={{
      height,
      background:
        'repeating-linear-gradient(135deg, var(--surface2) 0 10px, var(--surface3) 10px 20px)',
    }}
  >
    <span className="px-2 py-1 rounded-md bg-surface/90 text-[11px] font-medium text-text2">
      {label}
    </span>
  </button>
);

export const ApprovalsView = ({selectedShop}: any) => {
  const shopId = selectedShop?.shopId;
  const currency = selectedShop?.currency ?? 'Rs.';
  const link = useNetlyPayLink(shopId);
  const [params, setParams] = useSearchParams();

  // Review on a payment-requests row links straight to ?proof=AP-301.
  const [focusId, setFocusId] = useState<string | null>(params.get('proof'));
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [mode, setMode] = useState<'none' | 'amount' | 'reject'>('none');
  const [amount, setAmount] = useState('');
  const [reasonChip, setReasonChip] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [zoom, setZoom] = useState<{title: string; label: string} | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const flash = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2800);
  };

  const book = useNetlyPayFetch<NetlyPayData>(
    link.phase === 'linked' ? shopId : null,
    async (id) => {
      const [pending, requests, people] = await Promise.all([
        NetlyPayRepository.listPending(id),
        NetlyPayRepository.listRequests(id),
        NetlyPayRepository.listMembers(id),
      ]);
      return {
        approvals: pending?.payments ?? [],
        requests: requests?.requests ?? [],
        members: people?.members ?? [],
        programs: people?.programs ?? [],
        batches: people?.batches ?? [],
      };
    },
    [link.phase],
    (d) => (d?.approvals?.length ?? 0) === 0,
  );

  const data: NetlyPayData = book.data ?? {
    approvals: [],
    requests: [],
    members: [],
    programs: [],
    batches: [],
  };

  const rows = useMemo(() => approvalQueue(data, undefined, currency), [data, currency]);
  const summary = useMemo(() => queueSummary(data, currency), [data, currency]);

  // The focused proof may have just been approved out of the queue; fall back
  // to the oldest one left so staff keep working rather than facing a blank.
  const focus = focusId && rows.some((r) => r.id === focusId) ? focusId : rows[0]?.id ?? null;
  const panel = focus ? approvalPanel(data, focus, currency) : null;

  const selectedIds = rows.filter((r) => selected[r.id]).map((r) => r.id);
  const plan = useMemo(
    () => bulkApprovePlan(data, selectedIds, currency),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, selectedIds.join(','), currency],
  );
  const allOn = rows.length > 0 && rows.every((r) => selected[r.id]);

  const amountMinor = Math.round(Number(amount) * 100);
  const amountOk = Number.isFinite(amountMinor) && amountMinor > 0;

  const pickFocus = (id: string) => {
    setFocusId(id);
    setMode('none');
    setAmount('');
    setReasonChip(null);
    setNote('');
    const next = new URLSearchParams(params);
    next.set('proof', id);
    setParams(next, {replace: true});
  };

  const after = (message: string) => {
    flash(message);
    setMode('none');
    setAmount('');
    setReasonChip(null);
    setNote('');
    setFocusId(null);
    const next = new URLSearchParams(params);
    next.delete('proof');
    setParams(next, {replace: true});
    book.reload();
  };

  const approve = async (overrideMinor?: number) => {
    if (!shopId || !panel) return;
    setBusy(true);
    try {
      await NetlyPayRepository.verifyPayment(
        shopId,
        panel.id,
        overrideMinor != null ? {amountMinor: overrideMinor} : {},
      );
      const claimed = (data.approvals ?? []).find((a) => a.id === panel.id)?.amountMinor ?? 0;
      after(`Approved ${money(overrideMinor ?? claimed, currency)} from ${panel.member}`);
    } catch (e: any) {
      flash(e?.message ?? 'Could not approve the payment');
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!shopId || !panel || !reasonChip) return;
    setBusy(true);
    try {
      await NetlyPayRepository.rejectPayment(shopId, panel.id, {
        reasonCode: reasonChip,
        note: rejectReason(reasonChip, note),
      });
      after(`Rejected. ${panel.member} was told why.`);
    } catch (e: any) {
      flash(e?.message ?? 'Could not reject the payment');
    } finally {
      setBusy(false);
    }
  };

  const approveBulk = async () => {
    if (!shopId || !plan.approveIds.length) return;
    setBusy(true);
    try {
      await NetlyPayRepository.verifyBulk(shopId, plan.approveIds);
      setBulkOpen(false);
      setSelected({});
      after(
        `${plan.approveIds.length} ${plan.approveIds.length === 1 ? 'payment' : 'payments'} approved · ${plan.total}`,
      );
    } catch (e: any) {
      flash(e?.message ?? 'Could not approve the payments');
    } finally {
      setBusy(false);
    }
  };

  const listStates =
    book.phase === 'loading' ? (
      <Skeleton rows={6} />
    ) : book.phase === 'error' ? (
      <ErrorState message={book.error} onRetry={book.reload} />
    ) : book.phase === 'empty' ? (
      <EmptyState
        title="All caught up"
        text="Bank slips and LankaQR receipts members upload appear here, oldest first."
      />
    ) : null;

  return (
    <ScreenGate link={link} shopId={shopId} shop={selectedShop}>
      <div className="flex flex-col w-full p-7 gap-5 nl-rise">
        <div className="flex items-end justify-between gap-5 flex-wrap">
          <div>
            <h1 className="text-[26px] font-semibold text-text1 tracking-[-0.01em] leading-tight">
              Approvals
            </h1>
            <p className="text-[13px] text-text2 mt-1">
              {book.phase === 'loading' ? 'Loading…' : summary}
            </p>
          </div>
          {selectedIds.length > 0 && (
            <span className="flex items-center gap-2.5 flex-wrap">
              <span className="text-[12.5px] font-medium text-text1 font-mono tabular-nums">
                {selectionTotal(data, selectedIds, currency)}
              </span>
              <GhostButton onClick={() => setSelected({})}>Clear</GhostButton>
              <PrimaryButton onClick={() => setBulkOpen(true)}>
                Approve {selectedIds.length}
              </PrimaryButton>
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-4 items-start">
          {/* The queue */}
          <div className="bg-surface border border-borderc rounded-card overflow-hidden">
            {listStates}

            {book.phase === 'ready' && rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-surface2">
                    <tr>
                      <th className={`${th} w-10`}>
                        <input
                          type="checkbox"
                          checked={allOn}
                          onChange={() => {
                            if (allOn) {
                              setSelected({});
                              return;
                            }
                            const next: Record<string, boolean> = {};
                            rows.forEach((r) => {
                              next[r.id] = true;
                            });
                            setSelected(next);
                          }}
                          aria-label="Select every proof"
                          className="w-4 h-4 accent-[var(--brand)] cursor-pointer"
                        />
                      </th>
                      <th className={th}>PROOF</th>
                      <th className={th}>MEMBER · REQUEST</th>
                      <th className={`${th} text-right`}>AMOUNT</th>
                      <th className={th}>METHOD</th>
                      <th className={th}>PAID ON</th>
                      <th className={`${th} text-right`}>AGE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => pickFocus(row.id)}
                        className={`border-t border-borderc cursor-pointer transition-colors ${
                          row.id === focus ? 'bg-brand-soft/40' : 'hover:bg-surface2'
                        }`}
                      >
                        <td className={td} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={!!selected[row.id]}
                            onChange={() => setSelected({...selected, [row.id]: !selected[row.id]})}
                            aria-label={`Select the proof from ${row.member}`}
                            className="w-4 h-4 accent-[var(--brand)] cursor-pointer"
                          />
                        </td>
                        <td className={td}>
                          <span
                            className="w-10 h-10 rounded-lg border border-borderc flex items-center justify-center text-[10px] font-semibold text-text3"
                            style={{
                              background:
                                'repeating-linear-gradient(135deg, var(--surface2) 0 6px, var(--surface3) 6px 12px)',
                            }}
                            aria-hidden="true"
                          >
                            {row.kind}
                          </span>
                        </td>
                        <td className={td}>
                          <span className="block font-medium text-text1">
                            {row.member}
                            {row.isDuplicate && (
                              <span className="ml-2 px-1.5 py-0.5 rounded bg-warn-soft text-warn text-[10.5px] font-bold align-middle">
                                DUP?
                              </span>
                            )}
                          </span>
                          <span className="block text-[11.5px] text-text3">
                            <span className="font-mono">{row.requestId}</span> · {row.requestTitle}
                          </span>
                        </td>
                        <td className={`${td} font-mono tabular-nums text-right whitespace-nowrap`}>
                          {row.amount}
                        </td>
                        <td className={`${td} whitespace-nowrap`}>{row.method}</td>
                        <td className={`${td} font-mono tabular-nums text-text2 whitespace-nowrap`}>
                          {row.paidOn}
                        </td>
                        <td
                          className={`${td} text-right whitespace-nowrap font-medium ${
                            row.ageTone === 'danger'
                              ? 'text-neg'
                              : row.ageTone === 'warning'
                              ? 'text-warn'
                              : 'text-text2'
                          }`}
                        >
                          {row.age}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* The sticky panel */}
          <aside className="xl:sticky xl:top-5">
            {!panel ? (
              <div className="bg-surface border border-borderc rounded-card px-5 py-8 text-center">
                <p className="text-[13.5px] font-semibold text-text1">Nothing to review</p>
                <p className="text-[12.5px] text-text2 mt-1.5">
                  Pick a proof from the queue to see it full size with the request beside it.
                </p>
              </div>
            ) : (
              <div className="bg-surface border border-borderc rounded-card overflow-hidden">
                <div className="px-5 pt-4 pb-3 border-b border-borderc">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[14.5px] font-semibold text-text1 truncate">{panel.member}</p>
                      <p className="text-[11.5px] text-text3 font-mono">{panel.submitted}</p>
                    </div>
                    <span className="text-[11.5px] text-text3 font-mono whitespace-nowrap">
                      {panel.position}
                    </span>
                  </div>
                </div>

                <div className="px-5 py-4 flex flex-col gap-4">
                  <Proof
                    label={panel.proof}
                    height={230}
                    onClick={() => setZoom({title: `${panel.member} · ${panel.ref}`, label: panel.proof})}
                  />

                  {panel.isDuplicate && (
                    <div className="rounded-xl border border-warn/40 bg-warn-soft/50 px-3.5 py-3">
                      <p className="text-[12.5px] font-semibold text-warn">Possible duplicate</p>
                      <p className="text-[12.5px] text-text2 mt-0.5">{panel.duplicateText}</p>
                    </div>
                  )}

                  <dl className="flex flex-col gap-0">
                    {panel.rows.map((r) => (
                      <div
                        key={r.label}
                        className="flex items-start justify-between gap-3 py-2 border-b border-borderc last:border-b-0"
                      >
                        <dt className="text-[12.5px] text-text3 whitespace-nowrap">{r.label}</dt>
                        <dd
                          className={`text-[12.5px] text-right ${r.mono ? 'font-mono tabular-nums' : ''} ${
                            r.warn ? 'text-warn font-bold' : 'text-text1 font-medium'
                          }`}
                        >
                          {r.value}
                        </dd>
                      </div>
                    ))}
                  </dl>

                  {mode === 'none' && (
                    <div className="flex flex-col gap-2">
                      <PrimaryButton disabled={busy} onClick={() => approve()}>
                        Approve {panel.amount}
                      </PrimaryButton>
                      <GhostButton
                        onClick={() => {
                          setMode('amount');
                          setAmount(String(panel.owedMinor / 100));
                        }}
                      >
                        Different amount
                      </GhostButton>
                      <button
                        type="button"
                        onClick={() => {
                          setMode('reject');
                          setReasonChip(null);
                          setNote('');
                        }}
                        className="h-10 px-4 rounded-[9px] text-[13.5px] font-medium text-neg hover:bg-neg-soft transition-colors"
                      >
                        Reject
                      </button>
                    </div>
                  )}

                  {mode === 'amount' && (
                    <div className="flex flex-col gap-2.5">
                      <label className="flex flex-col gap-1.5">
                        <span className="text-[12px] font-semibold text-text2">
                          Amount that reached your account
                        </span>
                        <input
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          inputMode="decimal"
                          autoFocus
                          className={`${inputCls} font-mono ${amountOk ? '' : 'border-neg'}`}
                        />
                      </label>
                      <p className="text-[12px] text-text2">
                        {differentAmountNote(amountOk ? amountMinor : NaN, panel.owedMinor, currency)}
                      </p>
                      <div className="flex items-center gap-2.5">
                        <PrimaryButton disabled={!amountOk || busy} onClick={() => approve(amountMinor)}>
                          Approve
                        </PrimaryButton>
                        <GhostButton onClick={() => setMode('none')}>Back</GhostButton>
                      </div>
                    </div>
                  )}

                  {mode === 'reject' && (
                    <div className="flex flex-col gap-2.5">
                      <p className="text-[12px] font-semibold text-text2">Why is it being rejected?</p>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {REJECT_REASONS.map((r) => {
                          const on = reasonChip === r;
                          return (
                            <button
                              key={r}
                              type="button"
                              onClick={() => setReasonChip(r)}
                              className={`h-8 px-3 rounded-full text-[12px] font-medium border transition-colors ${
                                on
                                  ? 'bg-brand-soft border-brand text-brand'
                                  : 'bg-surface border-borderc text-text2 hover:bg-surface2'
                              }`}
                            >
                              {r}
                            </button>
                          );
                        })}
                      </div>
                      <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        placeholder="Anything else the member should know"
                        aria-label="Rejection note"
                        className="px-3 py-2.5 rounded-lg border border-borderc2 bg-surface text-[13px] text-text1 outline-none focus:border-brand transition-colors resize-none"
                      />
                      <div className="flex items-center gap-2.5">
                        <PrimaryButton tone="danger" disabled={!reasonChip || busy} onClick={reject}>
                          Reject and tell them
                        </PrimaryButton>
                        <GhostButton onClick={() => setMode('none')}>Back</GhostButton>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </aside>
        </div>

        {bulkOpen && (
          <Modal
            title={plan.title}
            sub="Each line is approved against its own request."
            width={560}
            onClose={() => setBulkOpen(false)}
            footer={
              <>
                <GhostButton onClick={() => setBulkOpen(false)}>Back</GhostButton>
                <PrimaryButton disabled={busy || !plan.approveIds.length} onClick={approveBulk}>
                  {plan.cta}
                </PrimaryButton>
              </>
            }
          >
            <div className="flex flex-col gap-3">
              {plan.hasDuplicate && (
                <div className="rounded-xl border border-warn/40 bg-warn-soft/50 px-3.5 py-3">
                  <p className="text-[12.5px] text-text2">{plan.duplicateNote}</p>
                </div>
              )}
              <div className="rounded-xl border border-borderc overflow-hidden">
                {plan.lines.map((l) => (
                  <div
                    key={l.id}
                    className={`flex items-center justify-between gap-3 px-3.5 py-2.5 border-b border-borderc last:border-b-0 ${
                      l.flag ? 'bg-surface2' : ''
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block text-[13px] text-text1 truncate">{l.member}</span>
                      <span className="block text-[11.5px] text-text3">
                        <span className="font-mono">{l.requestId}</span> · {l.method}
                      </span>
                    </span>
                    <span className="flex items-center gap-2.5 whitespace-nowrap">
                      {l.flag && (
                        <span className="px-1.5 py-0.5 rounded bg-surface3 text-text3 text-[10.5px] font-bold">
                          {l.flag}
                        </span>
                      )}
                      <span
                        className={`font-mono tabular-nums text-[13px] ${
                          l.flag ? 'text-text3 line-through' : 'text-text1'
                        }`}
                      >
                        {l.amount}
                      </span>
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 bg-surface2">
                  <span className="text-[13px] font-semibold text-text1">Total approved</span>
                  <span className="font-mono tabular-nums text-[13px] font-semibold text-text1">
                    {plan.total}
                  </span>
                </div>
              </div>
            </div>
          </Modal>
        )}

        {zoom && (
          <Modal title={zoom.title} width={640} onClose={() => setZoom(null)}>
            <Proof label={zoom.label} height={420} />
          </Modal>
        )}

        {toast && <Toast message={toast} />}
      </div>
    </ScreenGate>
  );
};

export default connect((state: any) => ({
  selectedShop: state.dashboard.get('selectedShop'),
}))(ApprovalsView);
